/**
 * Предпросмотр черновика курса (из конструктора) и защита несохранённых
 * правок.
 *
 * Предпросмотр открывается в НОВОЙ вкладке: /course/:id?preview=1 или
 * /lesson/:id?preview=1. Флаг запоминается в sessionStorage этой вкладки —
 * переходы курс → урок → урок остаются в предпросмотре; запросы
 * /wiki/education/* уходят с ?preview=1 (сервер отдаёт черновик только
 * редактору курсов и ничего не записывает). Вкладку редактора это не
 * трогает (sessionStorage у каждой вкладки свой).
 */

const PREVIEW_KEY = 'fk-wiki-preview';
const RETURN_KEY = 'fk-wiki-preview-return';

function ssGet(k: string): string | null {
  try {
    return window.sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function ssSet(k: string, v: string | null): void {
  try {
    if (v == null) window.sessionStorage.removeItem(k);
    else window.sessionStorage.setItem(k, v);
  } catch {
    /* приватный режим / запрет — работаем по URL */
  }
}

/** Прочитать ?preview=1&from=… из адреса (при загрузке вкладки и переходах). */
export function syncPreviewFromUrl(search: string = typeof window !== 'undefined' ? window.location.search : ''): void {
  const sp = new URLSearchParams(search);
  if (sp.get('preview') === '1') {
    ssSet(PREVIEW_KEY, '1');
    const from = sp.get('from');
    if (from && from.startsWith('/')) ssSet(RETURN_KEY, from);
  }
}

/** Вкладка в режиме предпросмотра черновика. */
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  return ssGet(PREVIEW_KEY) === '1' || new URLSearchParams(window.location.search).get('preview') === '1';
}

/** Куда вернуться — редактор курса. */
export function previewReturnUrl(): string {
  return ssGet(RETURN_KEY) || '/admin/courses';
}

/** Выйти из предпросмотра в этой вкладке. */
export function exitPreview(): void {
  ssSet(PREVIEW_KEY, null);
  ssSet(RETURN_KEY, null);
}

/** Добавить preview=1 к запросу ученика (только /wiki/education/*). */
export function withPreviewParam(path: string, preview = isPreviewMode()): string {
  if (!preview || !path.startsWith('/wiki/education/')) return path;
  if (/[?&]preview=1(&|$)/.test(path)) return path;
  return `${path}${path.includes('?') ? '&' : '?'}preview=1`;
}

/** Ссылка предпросмотра для новой вкладки (from — вернуться в редактор). */
export function previewHref(kind: 'course' | 'lesson', id: string, from: string): string {
  return `/${kind}/${encodeURIComponent(id)}?preview=1&from=${encodeURIComponent(from)}`;
}

/** Открыть предпросмотр в новой вкладке (редактор остаётся открытым). */
export function openPreview(kind: 'course' | 'lesson', id: string): void {
  const from = `${window.location.pathname}${window.location.search}`;
  window.open(previewHref(kind, id, from), '_blank', 'noopener');
}

// ─── Несохранённые правки ────────────────────────────────────────

const dirty = new Map<string, string>();

/** Отметить/снять «есть несохранённое» (key — источник, label — что именно). */
export function setUnsaved(key: string, label: string | null): void {
  if (label) dirty.set(key, label);
  else dirty.delete(key);
}

/** Есть ли несохранённые правки (и какие). */
export function unsavedLabels(): string[] {
  return Array.from(dirty.values());
}

/**
 * Спросить перед уходом из редактора (смена урока/вкладки, ссылка).
 * true — можно уходить (правок нет или пользователь согласился).
 */
export function confirmDiscard(): boolean {
  const labels = unsavedLabels();
  if (labels.length === 0) return true;
  const ok = window.confirm(`Есть несохранённые изменения (${labels.join(', ')}). Уйти без сохранения?`);
  if (ok) dirty.clear();
  return ok;
}

let installed = false;
/**
 * Глобальные сторожа (один раз на приложение): закрытие/перезагрузка вкладки
 * (beforeunload) и клики по внутренним ссылкам (capture-фаза — раньше
 * react-router: preventDefault отменяет переход Link).
 */
export function installUnsavedGuards(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('beforeunload', (e) => {
    if (dirty.size === 0) return;
    e.preventDefault();
    e.returnValue = '';
  });
  document.addEventListener(
    'click',
    (e) => {
      if (dirty.size === 0 || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      if (!confirmDiscard()) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}

// ─── Черновик окна в localStorage ────────────────────────────────

export interface LocalDraft<T> {
  savedAt: string;
  data: T;
}

export function loadLocalDraft<T>(key: string): LocalDraft<T> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as LocalDraft<T>;
    return v && typeof v === 'object' && 'data' in v ? v : null;
  } catch {
    return null;
  }
}

export function saveLocalDraft<T>(key: string, data: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({ savedAt: new Date().toISOString(), data }));
  } catch {
    /* квота / запрет — без автосохранения */
  }
}

export function clearLocalDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
